import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, combineLatest } from 'rxjs';
import { switchMap, tap, catchError } from 'rxjs/operators';
import { PatientFinancialService } from './patient-financial.service';
import { PatientFinancalBillingService } from './patient-financal-billing.service';
import { GroupService } from './group.service';
import { PatientService } from './patient.service';
import { SocketService } from './socket.service';
import { FileDownloadService } from './file-download.service';
import { resolveVatRate } from '../utils/vat.util';

export interface YocoPayment {
    id: string;
    yocoPaymentId: string;
    yocoCheckoutId: string;
    billingDocumentId: string;
    patientId: string;
    groupId: string;
    amount: number;
    currency: string;
    status: string;
    paymentMethod: string;
    metadata: any;
    yocoResponse: any;
    processed: boolean;
    processedAt: string;
    processedBy: string;
    createdAt: string;
    PatientBillingDocument?: any;
    Patient?: any;
    Provider?: any;
}

export interface UnprocessedPaymentsResponse {
    success: boolean;
    payments: YocoPayment[];
}

@Injectable({
    providedIn: 'root'
})
export class YocoPaymentProcessorService {
    private apiUrl = 'api';
    private unprocessedPaymentsSubject = new BehaviorSubject<YocoPayment[]>([]);
    public unprocessedPayments$ = this.unprocessedPaymentsSubject.asObservable();

    /**
     * Get appropriate description message for credit type payments
     */
    private getCreditDescription(paymentMethod: string, invoiceNumber: string): string {
        if (!paymentMethod) {
            return `Payment received for invoice ${invoiceNumber}`;
        }

        const method = paymentMethod.toLowerCase();
        
        if (method.includes('credit:')) {
            if (method.includes('pro bono')) {
                return `Pro bono credit applied for invoice ${invoiceNumber}`;
            } else if (method.includes('patient balance')) {
                return `Patient balance credit applied for invoice ${invoiceNumber}`;
            } else if (method.includes('bad debt')) {
                return `Bad debt written off for invoice ${invoiceNumber}`;
            } else if (method.includes('discounted')) {
                return `Discount credit applied for invoice ${invoiceNumber}`;
            } else if (method.includes('refund')) {
                return `Refund credit applied for invoice ${invoiceNumber}`;
            } else if (method.includes('other')) {
                return `Credit applied for invoice ${invoiceNumber}`;
            } else {
                // Generic credit
                return `Credit applied for invoice ${invoiceNumber}`;
            }
        }
        
        // Regular payment
        return `Payment received for invoice ${invoiceNumber}`;
    }

    private processingSubject = new BehaviorSubject<boolean>(false);
    public processing$ = this.processingSubject.asObservable();

    // Track processing status for different contexts
    private processingStatus = new Map<string, boolean>();

    private groupId?: string;
    private group?: any;
    private patientId?: string;
    private billingDocuments?: any[];

    constructor(
        private http: HttpClient,
        private groupService: GroupService,
        private patientService: PatientService,
        private patientFinancialService: PatientFinancialService,
        private patientBillingService: PatientFinancalBillingService,
        private socketService: SocketService,
        private fileDownloadService: FileDownloadService
    ) {

        combineLatest([
            this.groupService.selectedGroup$,
            this.patientService.currentPatient$,
            this.patientFinancialService.billingDocuments$
        ]).subscribe(([selectedGroup, currentPatient, billingDocuments]) => {
            if (selectedGroup && currentPatient && billingDocuments) {
                this.group = selectedGroup;
                this.groupId = selectedGroup.id;
                this.patientId = currentPatient.id;
                this.billingDocuments = billingDocuments;
                this.processYocoPaymentsForPatient(this.patientId!);
            }
        });

        this.setupSocketListeners();
    }

    private setupSocketListeners(): void {

        // Listener for when a new payment is captured via yoco webhook is added
        this.socketService.listen('yocoPaymentReceived', 'yoco-payment-processor-service').subscribe(payment => {
            if (payment.patientId === this.patientId) {
                this.processYocoPaymentsForPatient(payment.patientId);
            }
        });



    }

    // Auto-process Yoco payments for the selected group
    /* private async processYocoPaymentsForGroup(groupId: string): Promise<void> {
        try {
            console.log('Auto-processing Yoco payments for group:', groupId);
            const result = await this.processAllUnprocessedPaymentsForGroup(groupId);

            if (result.success > 0) {
                console.log(`Auto-processed ${result.success} Yoco payments for group ${groupId}`);
            }

            if (result.failed > 0) {
                console.warn(`Failed to process ${result.failed} Yoco payments for group ${groupId}`);
            }
        } catch (error) {
            console.error('Error auto-processing Yoco payments for group:', groupId, error);
        }
    } */

    // Auto-process Yoco payments for a specific patient
    private async processYocoPaymentsForPatient(patientId: string): Promise<void> {
        try {
            console.log('Auto-processing Yoco payments for patient:', patientId);
            const result = await this.processUnprocessedPaymentsForPatient(this.groupId!, patientId);

            if (result.success > 0) {
                console.log(`Auto-processed ${result.success} Yoco payments for patient ${patientId}`);
            }

            if (result.failed > 0) {
                console.warn(`Failed to process ${result.failed} Yoco payments for patient ${patientId}`);
            }
        } catch (error) {
            console.error('Error auto-processing Yoco payments for patient:', patientId, error);
        }
    }

    // Get unprocessed payments for a group
    getUnprocessedPayments(groupId: string): Observable<UnprocessedPaymentsResponse> {
        return this.http.get<UnprocessedPaymentsResponse>(`${this.apiUrl}/yoco/payments/${groupId}/unprocessed`)
            .pipe(
                tap(response => {
                    if (response.success) {
                        this.unprocessedPaymentsSubject.next(response.payments);
                    }
                })
            );
    }

    // Get unprocessed payments for a specific patient
    getUnprocessedPaymentsForPatient(groupId: string, patientId: string): Observable<UnprocessedPaymentsResponse> {
        return this.http.get<UnprocessedPaymentsResponse>(`${this.apiUrl}/yoco/payments/${groupId}/unprocessed?patientId=${patientId}`);
    }

    // Mark a payment as processed
    markPaymentAsProcessed(paymentId: string): Observable<any> {
        return this.http.put(`${this.apiUrl}/yoco/payments/${paymentId}/process`, {});
    }

    // Process a Yoco payment using the existing payment creation workflow
    async processYocoPayment(payment: YocoPayment): Promise<boolean> {
        try {
            this.processingSubject.next(true);

            //console.log('Processing Yoco payment:', payment);

            // Create payment data for the existing payment creation workflow
            const paymentData = {
                billingDocumentId: payment.billingDocumentId,
                patientId: payment.patientId,
                groupId: payment.groupId || payment.metadata?.groupId,
                amountReceived: payment.amount,
                paymentMethod: 'Yoco: Online', // Use the same format as capture-payment component
                notes: `Payment received via Yoco: Online`,
                allocations: payment.billingDocumentId ? [{
                    billingDocumentId: payment.billingDocumentId,
                    amount: payment.amount
                }] : []
            };

            //console.log('Payment data to send:', paymentData);

            // Use the existing createPayment method (same as submitNewPayment)
            const result = await this.patientFinancialService.createPayment(paymentData).toPromise();

            //console.log('Payment result:', result);

            if (result) {
                //console.log('Payment created successfully:', result);
                //console.log('Actual payment data:', result);

                // Generate payment receipts (same as submitNewPayment)
                //console.log('=== ABOUT TO CALL generatePaymentReceipts ===');
                await this.generatePaymentReceipts(result, paymentData, payment);
                //console.log('=== generatePaymentReceipts CALL COMPLETED ===');

                // Mark the Yoco payment as processed
                await this.markPaymentAsProcessed(payment.id).toPromise();

                // Remove from unprocessed list
                const currentPayments = this.unprocessedPaymentsSubject.value;
                const updatedPayments = currentPayments.filter(p => p.id !== payment.id);
                this.unprocessedPaymentsSubject.next(updatedPayments);

                //console.log('Yoco payment processed successfully:', payment.yocoPaymentId);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Error processing Yoco payment:', error);
            return false;
        } finally {
            this.processingSubject.next(false);
        }
    }

    // Generate payment receipts (same logic as submitNewPayment)
    private async generatePaymentReceipts(payment: any, paymentData: any, yocoPayment: YocoPayment): Promise<void> {

        //console.log('=== STARTING RECEIPT GENERATION ===');
        //console.log('Generating payment receipts for:', payment);
        //console.log('Payment data:', paymentData);
        //console.log('Yoco payment:', yocoPayment);

        try {
            //console.log('Getting patient and group information...');
            const patient = yocoPayment.Patient;
            const provider = yocoPayment.Provider;
            const group = this.group;

            //console.log('Patient found:', !!patient);
            //console.log('Group found:', !!group);

            if (!patient || !group) {
                console.error('Patient or group information not available for receipt generation');
                return;
            }

            //console.log('Looking for billing document with id:', yocoPayment.billingDocumentId);
            //console.log('Billing documents found:', this.billingDocuments);

            const billingDocument = this.billingDocuments?.find((doc: any) => doc.id === yocoPayment.billingDocumentId);
            //console.log('Billing document found:', !!billingDocument);

            if (!billingDocument) {
                console.error('No billing document found in billingDocuments$ for ID:', yocoPayment.billingDocumentId);
                return;
            }

            //console.log('Using billing document from billingDocuments$:', billingDocument);

            // Create receipt billing document data (same as submitNewPayment)
            //console.log('Creating receipt billing data...');
            const receiptBillingData = {
                patientId: patient.id,
                groupId: payment.groupId || paymentData.groupId,
                documentType: 'receipt' as 'receipt',
                issueDate: new Date(yocoPayment.createdAt),
                dueDate: new Date(yocoPayment.createdAt),
                subtotal: yocoPayment.amount,
                vatAmount: 0, // Receipts typically don't have VAT
                totalAmount: yocoPayment.amount,
                discountType: 'percentage' as 'percentage' | 'fixed',
                discount: 0,
                notes: `Payment receipt for invoice ${billingDocument?.billingNumber} - Yoco payment ${yocoPayment.yocoPaymentId}`,
                paymentId: payment.id, // Link receipt to payment
                providerId: provider?.id || billingDocument?.Provider?.id,
                originalInvoiceNumber: billingDocument?.billingNumber, // Use original invoice number for receipt numbering
                lineItems: [
                    {
                        description: this.getCreditDescription(paymentData.paymentMethod || '', billingDocument?.billingNumber || ''),
                        quantity: 1,
                        unitPrice: yocoPayment.amount,
                        totalAmount: yocoPayment.amount,
                        code: 'PAYMENT',
                        codeType: 'procedure',
                        date: new Date(yocoPayment.createdAt),
                        icdCodes: [],
                        modifiers: []
                    }
                ]
            };

            //console.log('Receipt billing data:', receiptBillingData);

            try {
                // Step 1: Create receipt billing document using new service (same as submitNewPayment)
                //console.log('=== CREATING RECEIPT BILLING DOCUMENT ===');
                const receiptResponse = await this.patientFinancialService.createBillingDocument(receiptBillingData).toPromise();
                const receiptDocument = receiptResponse?.data;
                //console.log('Receipt billing document created:', receiptDocument);

                if (!receiptDocument) {
                    throw new Error('Failed to create receipt billing document');
                }

                // Step 2: Generate PDF using the old service (same as submitNewPayment)
                // Use the billing document's outstanding balance (already updated by allocation processing)
                const outstandingBalance = parseFloat(billingDocument?.outstandingBalance || 0);
                //console.log('=== GENERATING PDF ===');
                const pdfBillingData = {
                    billingNumber: receiptDocument.billingNumber,
                    patientName: `${patient.name} ${patient.surname}`,
                    patient: patient,
                    paymentDate: new Date(yocoPayment.createdAt),
                    paymentMethod: paymentData.paymentMethod,
                    amountRecieved: yocoPayment.amount,
                    lineItems: billingDocument?.PatientBillingLineItems?.map((item: any) => ({
                        ...item,
                        balance: item.totalAmount, // Map totalAmount to balance for compatibility
                        tariffCode: item.code, // Map code to tariffCode for compatibility
                        tariffDescription: item.description, // Map description to tariffDescription for compatibility
                        icdCodes: item.icdCodes || [], // Include ICD codes if present
                        modifiers: item.modifiers || [] // Include modifiers if present
                    })) || [],
                    payments: [
                        {
                            linkedInvoice: billingDocument?.billingNumber,
                            allocatedAmount: yocoPayment.amount,
                            balance: outstandingBalance // Use the updated outstanding balance from billing document
                        }
                    ],
                    outstandingBalance: outstandingBalance, // Pass outstanding balance for PDF generation
                    notes: receiptBillingData.notes,
                    practiceInfo: {
                        practiceName: group.companyName || group.name,
                        practiceNumber: group.practiceNumber,
                        email: group.practiceEmail,
                        tel: group.practicePhone
                    },
                    user: billingDocument?.User || payment.User || payment.user || { name: 'System', surname: 'User', title: 'Dr' },
                    group: group,
                    provider: receiptDocument?.Provider || billingDocument?.Provider || yocoPayment.Provider,
                    hospital: billingDocument?.priceGroupId, // Use priceGroupId instead of hospitalId
                    doctorTariff: billingDocument?.DoctorTariff, // Include doctor tariff information
                    billingDocument: billingDocument, // Pass the complete billing document for reference
                    // Receipts inherit the underlying invoice's display style and VAT rate.
                    vatExclusive: billingDocument?.vatExclusive === true,
                    vatRate: resolveVatRate(billingDocument?.vatRate, group),
                };

                //console.log('PDF billing data:', pdfBillingData);

                // Generate PDF using the old service
                const pdfResponse = await this.patientBillingService.generateReceiptPDF(receiptDocument.id, pdfBillingData);
                //console.log('PDF generated, size:', pdfResponse.length);

                // Convert PDF bytes to blob (same as capture-payment component)
                //console.log('=== CREATING PDF BLOB ===');
                const pdfBlob = new Blob([pdfResponse as any], { type: 'application/pdf' });
                const fileName = `Receipt-${patient.name}-${patient.surname}_${Date.now()}.pdf`;

                //console.log('Uploading PDF to Azure...');

                // Upload PDF to new service (same as capture-payment component)
                const updatedReceiptDocument = await this.patientFinancialService.uploadBillingDocumentPDF(
                    receiptDocument.id,
                    payment.groupId || paymentData.groupId,
                    pdfBlob,
                    fileName
                ).toPromise();

                //console.log('Receipt PDF uploaded successfully:', updatedReceiptDocument);

                // Trigger platform-aware download
                await this.fileDownloadService.download(pdfBlob, fileName);

                //console.log('=== RECEIPT GENERATION COMPLETED SUCCESSFULLY ===');

            } catch (error) {
                console.error('=== ERROR IN RECEIPT GENERATION ===', error);
                // Don't throw error - payment was still captured successfully
            }

        } catch (error) {
            console.error('=== ERROR IN RECEIPT GENERATION OUTER TRY ===', error);
            // Don't throw error - payment was still captured successfully
        }
    }

    // Process all unprocessed payments for a group (called on login)
    /* async processAllUnprocessedPaymentsForGroup(groupId: string): Promise<{ success: number; failed: number }> {
        const processingKey = `group_${groupId}`;

        // Prevent duplicate processing
        if (this.processingStatus.get(processingKey)) {
            console.log('Already processing payments for group:', groupId);
            return { success: 0, failed: 0 };
        }

        this.processingStatus.set(processingKey, true);

        try {
            console.log('Auto-processing unprocessed payments for group:', groupId);

            const response = await this.getUnprocessedPayments(groupId).toPromise();

            if (!response?.success || !response.payments.length) {
                console.log('No unprocessed payments found for group:', groupId);
                return { success: 0, failed: 0 };
            }

            console.log(`Found ${response.payments.length} unprocessed payments for group:`, groupId);

            let successCount = 0;
            let failedCount = 0;

            for (const payment of response.payments) {
                try {
                    console.log('Processing Yoco payment:', payment);
                    const success = await this.processYocoPayment(payment);
                    if (success) {
                        successCount++;
                        console.log(`Auto-processed payment ${payment.yocoPaymentId} for group ${groupId}`);
                    } else {
                        failedCount++;
                        console.error(`Failed to auto-process payment ${payment.yocoPaymentId} for group ${groupId}`);
                    }
                } catch (error) {
                    failedCount++;
                    console.error(`Error auto-processing payment ${payment.yocoPaymentId}:`, error);
                }

                // Small delay between processing to avoid overwhelming the system
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            console.log(`Auto-processing complete for group ${groupId}: ${successCount} successful, ${failedCount} failed`);
            return { success: successCount, failed: failedCount };
        } catch (error) {
            console.error('Error in auto-processing for group:', groupId, error);
            return { success: 0, failed: 0 };
        } finally {
            this.processingStatus.delete(processingKey);
        }
    } */

    // Process unprocessed payments for a specific patient (called when patient file is loaded)
    async processUnprocessedPaymentsForPatient(groupId: string, patientId: string): Promise<{ success: number; failed: number }> {
        const processingKey = `patient_${groupId}_${patientId}`;

        // Prevent duplicate processing
        if (this.processingStatus.get(processingKey)) {
            console.log('Already processing payments for patient:', patientId);
            return { success: 0, failed: 0 };
        }

        this.processingStatus.set(processingKey, true);

        try {
            console.log('Auto-processing unprocessed payments for patient:', patientId);

            const response = await this.getUnprocessedPaymentsForPatient(groupId, patientId).toPromise();

            if (!response?.success || !response.payments.length) {
                console.log('No unprocessed payments found for patient:', patientId);
                return { success: 0, failed: 0 };
            }

            console.log(`Found ${response.payments.length} unprocessed payments for patient:`, patientId);

            let successCount = 0;
            let failedCount = 0;

            for (const payment of response.payments) {
                try {
                    //console.log('Processing Yoco payment for:', payment);
                    const success = await this.processYocoPayment(payment);
                    if (success) {
                        successCount++;
                        console.log(`Auto-processed payment ${payment.yocoPaymentId} for patient ${patientId}`);
                    } else {
                        failedCount++;
                        console.error(`Failed to auto-process payment ${payment.yocoPaymentId} for patient ${patientId}`);
                    }
                } catch (error) {
                    failedCount++;
                    console.error(`Error auto-processing payment ${payment.yocoPaymentId} for patient ${patientId}:`, error);
                }

                // Small delay between processing to avoid overwhelming the system
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            console.log(`Auto-processing complete for patient ${patientId}: ${successCount} successful, ${failedCount} failed`);
            return { success: successCount, failed: failedCount };
        } catch (error) {
            console.error('Error in auto-processing for patient:', patientId, error);
            return { success: 0, failed: 0 };
        } finally {
            this.processingStatus.delete(processingKey);
        }
    }

    // Legacy method for manual processing (kept for backward compatibility)
    /*  async processAllUnprocessedPayments(groupId: string): Promise<{ success: number; failed: number }> {
         return this.processAllUnprocessedPaymentsForGroup(groupId);
     } */

    // Start auto-processing (polling for new payments) - optional for continuous monitoring
    /*  startAutoProcessing(groupId: string, intervalMs: number = 30000): Observable<any> {
         return interval(intervalMs).pipe(
             switchMap(() => this.getUnprocessedPayments(groupId)),
             tap(response => {
                 if (response.success && response.payments.length > 0) {
                     console.log(`Found ${response.payments.length} unprocessed payments, starting auto-processing`);
                     this.processAllUnprocessedPaymentsForGroup(groupId);
                 }
             }),
             catchError(error => {
                 console.error('Error in auto-processing:', error);
                 return [];
             })
         );
     } */

    // Get current unprocessed payments count
    getUnprocessedCount(): number {
        return this.unprocessedPaymentsSubject.value.length;
    }

    // Clear unprocessed payments
    clearUnprocessedPayments(): void {
        this.unprocessedPaymentsSubject.next([]);
    }

    // Check if currently processing for a specific context
    isProcessing(context: string): boolean {
        return this.processingStatus.get(context) || false;
    }
} 